import { useState } from 'react';
import { Link } from 'react-router';
import { t } from '../i18n/index.ts';
import { day } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { VENDOR } from '../lib/vendor.ts';
import { Icon } from './Icon.tsx';
import { IconButton } from './ui.tsx';

const DISMISS_KEY = 'erp.plan.banner';
const daysLeft = (until: string) => Math.floor((Date.parse(`${until}T00:00:00Z`) + 86_400_000 - Date.now()) / 86_400_000);

/**
 * Tells the business where its ERP PRO plan stands: everyone sees read-only (because saving
 * fails), and whoever runs the business also sees a late payment and the last days of a trial.
 */
export function PlanBanner() {
  const { me, can } = useSession();
  const [hidden, setHidden] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const plan = me?.plan;
  if (!plan) return null;
  let tone: 'danger' | 'warn' | 'info';
  let text: string;
  if (plan.read_only) {
    tone = 'danger';
    text = t('plan.read_only');
  } else if (!can('company.manage') || hidden) {
    return null;
  } else if (plan.state === 'past_due') {
    tone = 'warn';
    text = t('plan.past_due', { date: plan.until ? day(plan.until) : '–' });
  } else if (plan.state === 'trial' && plan.until && daysLeft(plan.until) <= 7) {
    tone = 'info';
    const n = Math.max(daysLeft(plan.until), 0);
    text = n === 0 ? t('plan.trial_today') : t(n === 1 ? 'plan.trial_left.one' : 'plan.trial_left.other', { n });
  } else {
    return null;
  }
  return (
    <div className={`plan-banner plan-banner-${tone}`} role={tone === 'info' ? 'status' : 'alert'}>
      <Icon name={tone === 'danger' ? 'lock' : tone === 'warn' ? 'alert' : 'clock'} size={18} />
      <span>
        {text}{' '}
        {can('billing.manage') ? (
          <Link className="plan-banner-contact" to="/billing">
            {t(plan.state === 'trial' ? 'plan.choose' : 'plan.pay')}
          </Link>
        ) : (
          <a className="plan-banner-contact" href={`mailto:${VENDOR.product.email}`}>
            {t('plan.contact', { email: VENDOR.product.email })}
          </a>
        )}
      </span>
      {plan.read_only ? null : (
        <IconButton
          icon="x"
          label={t('common.close')}
          onClick={() => {
            try {
              sessionStorage.setItem(DISMISS_KEY, '1');
            } catch {
              /* private mode */
            }
            setHidden(true);
          }}
        />
      )}
    </div>
  );
}
